import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiLayers, FiTag } from "react-icons/fi";
import type { DataMartNodeData } from "./types";
import { SchemaTableName } from "../../mapping/components/SchemaTableName";

/** Same vocabulary the lineage view uses for these constructs. */
const dimensionKindLabel = (tableConstruct?: string | null): string => {
  const normalized = (tableConstruct ?? "").toLowerCase();
  if (normalized.startsWith("scd1")) {
    return "SCD Type 1";
  }
  if (normalized.startsWith("scd2")) {
    return "SCD Type 2";
  }
  return "Descriptive attributes";
};

export function DimensionNode({ data, selected }: NodeProps<DataMartNodeData>): JSX.Element {
  const { entity, onSelect } = data;
  if (entity.kind !== "dimension") {
    return <></>;
  }
  const isInternalRegistered =
    entity.record.target_entity_dm_origin === "internal_registered" ||
    entity.record.source_entity_id != null;
  const attributeCount = entity.columns.filter(
    (column) => column.role === "attribute" || column.role === "dimension"
  ).length;
  const connectorPositions = [Position.Top, Position.Right, Position.Bottom, Position.Left];
  const offset = 10;
  const buildHandleStyle = (position: Position, type: "source" | "target") => {
    if (position === Position.Top || position === Position.Bottom) {
      return {
        left: `calc(50% ${type === "source" ? `+ ${offset}px` : `- ${offset}px`})`
      };
    }
    return {
      top: `calc(50% ${type === "source" ? `+ ${offset}px` : `- ${offset}px`})`
    };
  };
  const renderHandle = (position: Position) => (
    <React.Fragment key={`dimension-${position}`}>
      <Handle
        type="target"
        id={`dimension-${position}-target`}
        position={position}
        className="!tw-h-3 !tw-w-3 !tw-border tw-border-emerald-200 !tw-bg-white/70"
        style={buildHandleStyle(position, "target")}
      />
      <Handle
        type="source"
        id={`dimension-${position}-source`}
        position={position}
        className="!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw-bg-emerald-500"
        style={buildHandleStyle(position, "source")}
      />
    </React.Fragment>
  );

  return (
    <div
      onClick={(event) => {
        if (event.shiftKey || event.metaKey || event.ctrlKey) {
          return;
        }
        onSelect(entity.id);
      }}
      className={`tw-min-w-[200px] tw-cursor-grab tw-rounded-3xl tw-border tw-border-emerald-200 tw-bg-gradient-to-br tw-from-white tw-via-emerald-50 tw-to-emerald-100 tw-p-4 tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1 ${
        selected
          ? "tw-ring-2 tw-ring-emerald-400 tw-ring-offset-2"
          : "tw-ring-1 tw-ring-emerald-200/60"
      }`}
    >
      {connectorPositions.map((position) => renderHandle(position))}
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-emerald-500 tw-text-white tw-shadow-md">
          <FiLayers className="tw-h-4 tw-w-4" />
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-slate-900">Dimension</p>
          <p className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-emerald-700">
            {dimensionKindLabel(entity.record.table_construct)}
          </p>
        </div>
        {isInternalRegistered ? (
          <div className="tw-ml-auto tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-emerald-100 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-emerald-700">
            Internal
          </div>
        ) : null}
      </div>
      <div className="tw-mt-3">
        <SchemaTableName
          schema={entity.record.target_schema}
          table={entity.record.target_entity_dm_name}
          layout="stacked"
          className="tw-block tw-font-mono tw-text-[13px] tw-font-semibold tw-leading-5 tw-text-slate-900 tw-break-words"
        />
        <div className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-white/60 tw-bg-white/90 tw-px-2 tw-py-1 tw-text-[10px] tw-font-semibold tw-text-slate-600">
          <FiTag className="tw-h-3 tw-w-3 tw-text-emerald-600" />
          <span>{attributeCount} attributes</span>
        </div>
      </div>
    </div>
  );
}
